/**
 * useMe - the SPA's first call after login. Returns the authenticated
 * user's positions + admin flag, drives the AppShell's position-queue
 * widget and every position-gated control.
 *
 * Lives under shared/api/ (not shared/auth/) because it's a thin
 * React-Query wrapper on a backend endpoint, not an auth primitive.
 * The auth primitives (token store, login flow, refresh) land with
 * the Keycloak adapter in a later P1.x slot.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { meKeys } from "shared/api/queryClient";
import type { MeOut, Position } from "shared/api/types";

export function useMe(enabled = true): UseQueryResult<MeOut, ApiError> {
  return useQuery({
    queryKey: meKeys.all,
    queryFn: () => api.get<MeOut>("/v1/me"),
    // /v1/me is cheap + stable for the session - cache 5 minutes.
    staleTime: 5 * 60_000,
    // AppShell mounts this hook above its auth-status gate (hooks are
    // unconditional), so without `enabled` the query fires token-less
    // during keycloak init and 401-retries until auth settles. Every
    // consumer inside the authed <Outlet /> keeps the default.
    enabled,
  });
}

/**
 * May this person ACT in this position -- admin included.
 *
 * Round 9 E3 -- the wording here used to be "true if the user holds
 * this position OR is admin", which invites the mistake it caused:
 * somebody asking "does this person hold the preparer seat" searches
 * for "holds", finds this, and gets a helper that answers yes for
 * every admin. That is how a fold added in round 8 to keep the editor
 * out of an administrator's way never rendered for an administrator,
 * and the finding reached its sixth round looking fixed.
 *
 * This is the AUTHORITY question and the right one for gating a verb.
 * For the IDENTITY question -- whose name goes on the record -- use
 * `holdsSeat` below.
 */
export function hasPosition(me: MeOut | undefined, position: Position): boolean {
  if (!me) return false;
  return me.is_admin || me.positions.includes(position);
}

/**
 * A2/A4 -- does this person HOLD the seat, admin excluded.
 *
 * `hasPosition` admits an admin, which is right for reaching a surface
 * and wrong for offering an act that puts a name on a record. The walk
 * found the head of ops res recording the second line's challenge
 * under her own name, and the owner's register offering him three
 * scoring verbs the server refuses.
 *
 * The distinction mirrors the server exactly: `ownership_for` and
 * `require_second_line` both read the RAW position set for the same
 * reason. Reading a ceremony is not signing one.
 */
export function holdsSeat(me: MeOut | undefined, position: Position): boolean {
  if (!me) return false;
  return me.positions.includes(position);
}

/** The realm role itself, admin excluded, for the same reason. */
export function holdsRealmRole(me: MeOut | undefined, role: string): boolean {
  if (!me) return false;
  return (me.realm_roles ?? []).includes(role);
}
